import { sql } from "drizzle-orm";
import {
  boolean,
  check,
  index,
  integer,
  jsonb,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  varchar,
} from "drizzle-orm/pg-core";
import {
  organisations,
  schoolAvailabilityStatusEnum,
  schoolPupils,
} from "./schemaBase.js";

const id = () =>
  varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`);

const organisationId = () =>
  varchar("organisation_id")
    .notNull()
    .references(() => organisations.id, { onDelete: "restrict" });

const createdAt = () => timestamp("created_at", { withTimezone: true }).notNull().defaultNow();
const updatedAt = () => timestamp("updated_at", { withTimezone: true }).notNull().defaultNow();

/**
 * Canonical School session descriptor after migrations through 0071.
 *
 * The base schema predates 0070's persisted tutor subject. Keeping the complete
 * descriptor here lets @wmf/shared/schema expose one migration-aligned table
 * without rewriting the large historical schema module.
 */
export const schoolSessions = pgTable(
  "school_sessions",
  {
    id: id(),
    organisationId: organisationId(),
    schoolKey: text("school_key").notNull(),
    startsAt: timestamp("starts_at", { withTimezone: true }).notNull(),
    endsAt: timestamp("ends_at", { withTimezone: true }).notNull(),
    status: text("status").notNull(),
    availabilityStatus: schoolAvailabilityStatusEnum("availability_status")
      .notNull()
      .default("pending"),
    availabilityError: text("availability_error"),
    availabilityUpdatedAt: timestamp("availability_updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    verifiedProviderId: integer("verified_provider_id").notNull(),
    serviceId: integer("service_id"),
    serviceName: text("service_name"),
    schoolTutorSubject: text("school_tutor_subject"),
    sourceKind: text("source_kind"),
    sourceId: text("source_id"),
    sourceExternalId: text("source_external_id"),
    sourceChecksum: text("source_checksum"),
    metadata: jsonb("metadata"),
    createdBySubject: text("created_by_subject"),
    updatedBySubject: text("updated_by_subject"),
    createdAt: createdAt(),
    updatedAt: updatedAt(),
    version: integer("version").notNull().default(1),
  },
  (t) => [
    index("school_sessions_org_schedule_idx").on(t.organisationId, t.startsAt, t.endsAt),
    index("school_sessions_org_school_idx").on(t.organisationId, t.schoolKey, t.startsAt),
    index("school_sessions_tutor_subject_idx").on(
      t.organisationId,
      t.schoolTutorSubject,
      t.startsAt,
    ),
    uniqueIndex("school_sessions_org_source_idx").on(
      t.organisationId,
      t.sourceKind,
      t.sourceId,
    ),
    check("school_sessions_date_order", sql`${t.endsAt} > ${t.startsAt}`),
    check("school_sessions_version_positive", sql`${t.version} > 0`),
  ],
);

/** Canonical School note descriptor after family publication parity migrations. */
export const schoolNotes = pgTable(
  "school_notes",
  {
    id: id(),
    organisationId: organisationId(),
    sessionId: varchar("session_id")
      .notNull()
      .references(() => schoolSessions.id, { onDelete: "restrict" }),
    pupilId: varchar("pupil_id")
      .notNull()
      .references(() => schoolPupils.id, { onDelete: "restrict" }),
    authorSubject: text("author_subject").notNull(),
    title: text("title"),
    content: text("content").notNull(),
    publishedAt: timestamp("published_at", { withTimezone: true }),
    familyNotifiedAt: timestamp("family_notified_at", { withTimezone: true }),
    /** Predates the Hub email switch; manual Outlook delivery is unverified. */
    legacyOutlookDelivery: boolean("legacy_outlook_delivery").notNull().default(false),
    sourceKind: text("source_kind"),
    sourceId: text("source_id"),
    sourceChecksum: text("source_checksum"),
    createdAt: createdAt(),
    updatedAt: updatedAt(),
    version: integer("version").notNull().default(1),
  },
  (t) => [
    index("school_notes_org_pupil_idx").on(t.organisationId, t.pupilId, t.createdAt),
    index("school_notes_session_idx").on(t.sessionId),
    uniqueIndex("school_notes_org_source_idx").on(t.organisationId, t.sourceKind, t.sourceId),
    check("school_notes_version_positive", sql`${t.version} > 0`),
  ],
);

/** Canonical School practice-assignment descriptor after migration 0072. */
export const schoolPracticeTodos = pgTable(
  "school_practice_todos",
  {
    id: id(),
    organisationId: organisationId(),
    pupilId: varchar("pupil_id")
      .notNull()
      .references(() => schoolPupils.id, { onDelete: "restrict" }),
    title: text("title").notNull(),
    description: text("description"),
    completed: boolean("completed").notNull().default(false),
    completedAt: timestamp("completed_at", { withTimezone: true }),
    teacherNotes: text("teacher_notes"),
    readyForReviewAt: timestamp("ready_for_review_at", { withTimezone: true }),
    withdrawnAt: timestamp("withdrawn_at", { withTimezone: true }),
    createdBySubject: text("created_by_subject").notNull(),
    completedBySubject: text("completed_by_subject"),
    sourceId: text("source_id"),
    suggestedMinutes: integer("suggested_minutes"),
    metronome: jsonb("metronome"),
    sourceSessionId: varchar("source_session_id").references(() => schoolSessions.id, {
      onDelete: "set null",
    }),
    repertoireTitle: text("repertoire_title"),
    teachingContextSubject: text("teaching_context_subject"),
    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => [
    index("school_practice_todos_pupil_idx").on(t.organisationId, t.pupilId, t.completed),
    index("school_practice_todos_source_session_idx").on(
      t.organisationId,
      t.pupilId,
      t.sourceSessionId,
      t.createdAt,
    ),
    index("school_practice_todos_teaching_context_idx").on(
      t.organisationId,
      t.pupilId,
      t.teachingContextSubject,
      t.completed,
      t.createdAt,
    ),
    check("school_practice_todos_title_present", sql`length(btrim(${t.title})) > 0`),
    check(
      "school_practice_todos_completion_complete",
      sql`${t.completed} = false or ${t.completedAt} is not null`,
    ),
    check(
      "school_practice_todos_suggested_minutes_range",
      sql`${t.suggestedMinutes} is null or (${t.suggestedMinutes} >= 1 and ${t.suggestedMinutes} <= 480)`,
    ),
  ],
);

/**
 * Files a tutor attached to a School lesson write-up.
 *
 * Deliberately its own table beside `lesson_attachments` rather than a
 * `school_note_id` column added to it. School notes are School-owned records
 * with their own authority, and a shared table would be the one place a
 * private lesson query could reach a School pupil's evidence by forgetting a
 * predicate. Two tables cannot be joined by accident.
 *
 * Bytes live in object storage; `object_key` never leaves the Hub, which
 * re-checks School teaching authority and redirects to a short-lived URL.
 */
export const schoolNoteAttachments = pgTable(
  "school_note_attachments",
  {
    id: id(),
    organisationId: organisationId(),
    noteId: varchar("note_id")
      .notNull()
      .references(() => schoolNotes.id, { onDelete: "cascade" }),
    fileName: text("file_name").notNull(),
    fileType: text("file_type"),
    fileSize: integer("file_size"),
    objectKey: text("object_key").notNull(),
    resourceType: text("resource_type"),
    externalUrl: text("external_url"),
    sourceKind: text("source_kind"),
    sourceId: text("source_id"),
    shareable: boolean("shareable").notNull().default(true),
    /** Who attached it, in the School domain's subject form. */
    uploadedBySubject: text("uploaded_by_subject"),
    createdAt: createdAt(),
  },
  (t) => [
    index("school_note_attachments_note_idx").on(t.organisationId, t.noteId, t.createdAt),
    uniqueIndex("school_note_attachments_source_idx").on(t.noteId, t.sourceKind, t.sourceId),
    check("school_note_attachments_file_name_present", sql`length(btrim(${t.fileName})) > 0`),
    check(
      "school_note_attachments_file_size_positive",
      sql`${t.fileSize} is null or ${t.fileSize} > 0`,
    ),
  ],
);
